import type { ComponentProps, ReactNode } from "react";
import { Pressable, ScrollView, StyleSheet } from "react-native";
import { Icon, Text, useTheme } from "react-native-paper";
import Box from "./Box";

type FilterIcon = ComponentProps<typeof Icon>["source"];

type FilterItem<T extends string = string> = {
	icon?: FilterIcon;
	value: T;
	label: ReactNode;
};

type Props<T extends string = string> = {
	value: T;
	onValueChange: (value: T) => void;
	filters: FilterItem<T>[];
};

export const FilterChipGroup = <T extends string = string>({
	value,
	onValueChange,
	filters,
}: Props<T>) => {
	const theme = useTheme();

	return (
		<ScrollView
			horizontal
			showsHorizontalScrollIndicator={false}
			contentContainerStyle={styles.container}
		>
			{filters.map((filter) => {
				const selected = filter.value === value;
				const icon = filter.icon ?? (selected ? "check" : undefined);

				return (
					<Pressable
						key={filter.value}
						accessibilityRole="radio"
						accessibilityState={{ checked: selected }}
						onPress={() => onValueChange(filter.value)}
						style={({ pressed }) => [
							styles.chip,
							{
								borderRadius: theme.roundness * 4,
								borderColor: theme.colors.outline,
								backgroundColor: selected
									? theme.colors.secondaryContainer
									: theme.colors.surface,
								opacity: pressed ? 0.9 : 1,
							},
						]}
					>
						<Box
							flexDirection="row"
							alignItems="center"
							justifyContent="center"
							gap={8}
						>
							{icon ? (
								<Icon
									source={icon}
									size={18}
									color={
										selected
											? theme.colors.onSecondaryContainer
											: theme.colors.onSurfaceVariant
									}
								/>
							) : null}
							{typeof filter.label === "string" ||
							typeof filter.label === "number" ? (
								<Text
									variant="labelLarge"
									style={{
										color: selected
											? theme.colors.onSecondaryContainer
											: theme.colors.onSurfaceVariant,
									}}
								>
									{filter.label}
								</Text>
							) : (
								filter.label
							)}
						</Box>
					</Pressable>
				);
			})}
		</ScrollView>
	);
};

const styles = StyleSheet.create({
	container: {
		flexDirection: "row",
		gap: 8,
		alignItems: "center",
	},
	chip: {
		flexShrink: 0,
		minHeight: 40,
		paddingHorizontal: 12,
		paddingVertical: 8,
		borderWidth: 1,
		justifyContent: "center",
	},
});
